/** Data-fetching + theme hooks shared across panels. */
import { useCallback, useEffect, useState } from 'react';
import { useApi } from './api';
import type { Health, ListResp } from './types';

function errText(status: number, data: unknown): string {
  if (data && typeof data === 'object' && 'error' in data) return String((data as { error: unknown }).error);
  return `HTTP ${status}`;
}

interface Async<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/** GET a resource. Pass `null` to skip fetching (e.g. behind the gate). */
export function useResource<T>(path: string | null): Async<T> {
  const { request } = useApi();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    request<T>('GET', path)
      .then((r) => {
        if (cancelled) return;
        if (r.ok) setData(r.data);
        else setError(errText(r.status, r.data));
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, nonce, request]);

  return { data, loading, error, reload };
}

/** Paginated list convenience wrapper. */
export function useList<T>(path: string | null): Async<ListResp<T>> {
  return useResource<ListResp<T>>(path);
}

/** Liveness + source registry from /health (no auth needed). */
export function useHealth(enabled = true): Async<Health> {
  return useResource<Health>(enabled ? '/health' : null);
}

// ---- theme ---------------------------------------------------------------

const LS_THEME = 'cati.theme';
export type Theme = 'dark' | 'light';

function applyTheme(t: Theme) {
  document.documentElement.setAttribute('data-theme', t);
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(LS_THEME) as Theme) || 'dark');
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(LS_THEME, theme);
  }, [theme]);
  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);
  return [theme, toggle];
}
